import {assert} from 'Asserts';
import * as metrics from 'metrics/Metrics';
import {Editor} from 'notebook/mediocre/EditorTypes';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {TableIndex} from 'notebook/tables/TableConstants';
import deburrString from 'util/deburrString';
import escapeRegExp from 'util/escapeRegExp';

export class TextMatcher {
  private regExpLockId: string | null = null;
  // Wrap usages of RegExp between acquireRegExpLock / releaseRegexpLock; because RegExp is stateful
  // and used within a generator context, we are susceptible to reentrancy bugs.
  private regExp: RegExp;
  query: string;

  constructor(query: string) {
    query = deburrString(query);
    this.query = query;
    this.regExp = new RegExp(escapeRegExp(query), 'igu');
  }

  private acquireRegExpLock(requester: string): void {
    assert(!this.regExpLockId, `${requester} cannot lock on regExp - already held by ${this.regExpLockId}`);
    this.regExpLockId = requester;
  }

  private releaseRegexpLock(): void {
    this.regExpLockId = null;
  }

  /**
   * NOTE: findAllMatches is not reentrant: while enumerating findAllMatches, it is not safe to call
   * findAllMatches again; otherwise, duplicate results will be yielded.
   */
  *findAllMatches(haystack: string): Generator<RegExpExecArray> {
    const deburredHaystack = deburrString(haystack);
    // TODO(BNCH-42613): Use these metrics to improve our handling of special characters in searches
    if (deburredHaystack !== haystack) {
      metrics.count('bnch.notebook.search.deburredHaystack.items');
      if (deburredHaystack.length !== haystack.length) {
        metrics.count('bnch.notebook.search.deburredHaystack.lengthMismatch');
      }
    }
    let match;
    this.acquireRegExpLock('findAllMatches');
    try {
      this.regExp.lastIndex = 0;
      // oxlint-disable-next-line no-cond-assign -- pattern for iterating over the results of a global regExp
      while ((match = this.regExp.exec(deburredHaystack))) {
        yield match;
      }
    } finally {
      this.releaseRegexpLock();
    }
  }

  /**
   * NOTE: hasMatch is not reentrant: while enumerating findAllMatches, it is not safe to call hasMatch;
   * otherwise, duplicate results will be yielded.
   */
  hasMatch(haystack: string): boolean {
    this.acquireRegExpLock('hasMatch');
    try {
      this.regExp.lastIndex = 0;
      return this.regExp.test(haystack);
    } finally {
      this.releaseRegexpLock();
    }
  }
}

export interface MediocreSearchResult {
  // A path through the component hierarchy where the search result will be rendered, e.g. an item has a
  // component that renders two column layout containing two grids, where the search result is found in the
  // left grid in the second row, third column, could be represented as:
  //     ['itemCid1', 'left', 1, 2]
  index: Array<string | number>;
  data?: unknown; // Component-specific data to help render the result.
  uuid: string; // A UUID that a rendered search result should be tagged with we can scroll it into view.
  selection: MetadataForRestore<TableIndex> | {selection: BenchlingSelection};
}

export function getSearchResultIndex(result: MediocreSearchResult): Array<string | number> {
  return result.index;
}

export default function* getSearchResults<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  editor: TEditor,
  docRoot: TRoot,
  items: Array<TItem>,
  focusedItem: TItem | null,
  query: string,
  searchOverFormulas: boolean,
  isTableCollapsedById: {[cid: string]: boolean | undefined}
): Generator<MediocreSearchResult> {
  const matcher = new TextMatcher(query);
  for (const item of items) {
    const cid = editor.props.getCid(item);
    const type = editor.props.getType(item);
    const plugin = editor.getPluginByType(type);
    if (plugin.component?.getIsInaccessible?.(item)) {
      continue;
    }
    // If performance becomes an issue, we may want to consider memoizing the search results
    const results = plugin.getSearchResults?.(
      item,
      matcher,
      searchOverFormulas,
      editor.getComputedTables(docRoot),
      isTableCollapsedById[cid]
    );
    for (const result of results ?? []) {
      yield result;
    }
  }
  if (focusedItem != null) {
    const type = editor.props.getType(focusedItem);
    const plugin = editor.getPluginByType(type);
    if (!plugin.component?.getIsInaccessible?.(focusedItem)) {
      const results = plugin.getSearchResults?.(
        focusedItem,
        matcher,
        searchOverFormulas,
        editor.getComputedTables(docRoot),
        false
      );
      for (const result of results ?? []) {
        yield result;
      }
    }
  }
}
